import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  WaBriefingAssignable,
  WaBriefingDayPart,
  WaBriefingFindTimeData,
  WaBriefingList,
  WaBriefingListResponse,
  WaBriefingPeopleResponse,
  WaBriefingSuggestion,
  WaBriefingSuggestionResponse,
  WaBriefingTodo,
  WaBriefingTodoResponse,
  WaBriefingWork,
} from '../../types/waBriefing';
import DreamMarque from '../common/DreamMarque';
import {
  addDays,
  dueLabel,
  formatDay,
  lateLabel,
  nextWeek,
  thisWeekend,
} from './morningListDates';
import '../wa-briefing-page.scss';
import '../wa-briefing-todos.scss';
import './morning-list.scss';

/**
 * The Morning List: Today, the user's private lists and Later, with Who /
 * When / List on a picked to-do, and what they gave other people folded into
 * "Assigned to others". Shared by the phone page (token) and the signed-in
 * home (desktop and phone); each supplies how it reaches the API.
 */

export type MorningListPost = <T>(
  path: string,
  body: Record<string, unknown>,
) => Promise<{
  ok: boolean;
  status: number;
  payload: (T & { error?: string }) | null;
}>;

export interface MorningListData {
  date: string;
  language: string;
  assistantName: string | null;
  todos: WaBriefingTodo[];
  lists: WaBriefingList[];
  suggestions?: WaBriefingSuggestion[];
}

interface Props {
  data: MorningListData;
  update: (patch: Partial<MorningListData>) => void;
  post: MorningListPost;
  initialListId?: string | null;
  /** Rendered inside a phone frame: always the mobile layout. */
  phone?: boolean;
  /** Inside the signed-in desktop home: no own header, lists on the left. */
  embedded?: boolean;
  /** The page link expired (phone page only). */
  onExpired?: () => void;
}

const TODAY = 'today';
const LATER = 'later';
const ASK_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const DAY_PARTS: { id: WaBriefingDayPart; name: string }[] = [
  { id: 'morning', name: 'Morning' },
  { id: 'after_lunch', name: 'After lunch' },
  { id: 'afternoon', name: 'Afternoon' },
  { id: 'end_of_day', name: 'End of day' },
];

const AVATAR_COLOURS = [
  '#e0735a',
  '#5a8be0',
  '#3f9f86',
  '#b06ad0',
  '#d0a13c',
  '#5f6b7d',
];

type Mode = 'who' | 'when' | 'list' | 'ask' | null;

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

const workLine = (state: WaBriefingWork['state'], assistant: string) => {
  switch (state) {
    case 'waiting':
      return 'Waiting for you';
    case 'waiting_others':
      return 'Waiting to hear back';
    case 'stopped':
      return 'Stopped';
    case 'done':
      return `${assistant} says it's done`;
    case 'failed':
      return `${assistant} couldn't do it`;
    default:
      return `${assistant} is working on it`;
  }
};

const Tick = () => (
  <svg
    viewBox='0 0 12 12'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M2.5 6.3l2.3 2.3 4.7-5' />
  </svg>
);

const Caret = () => (
  <svg
    viewBox='0 0 10 6'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.5'
    aria-hidden='true'
  >
    <path d='M1 1l4 4 4-4' />
  </svg>
);

const Gear = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.7'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <circle cx='12' cy='12' r='3' />
    <path d='M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z' />
  </svg>
);

const Search = () => (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    aria-hidden='true'
  >
    <circle cx='11' cy='11' r='6.5' />
    <path d='M16 16l4.5 4.5' />
  </svg>
);

const Avatar: React.FC<{ userId: string; name: string; small?: boolean }> = ({
  userId,
  name,
  small,
}) => {
  const hash = userId
    .split('')
    .reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 0);
  return (
    <span
      className={`ml__avatar${small ? ' ml__avatar--small' : ''}`}
      style={{
        background: AVATAR_COLOURS[Math.abs(hash) % AVATAR_COLOURS.length],
      }}
      aria-hidden='true'
    >
      {name.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
};

/** Set once the user has picked a to-do, so the how-to hint stops showing. */
const Dots: React.FC = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
    <circle cx='5' cy='12' r='1.8' fill='currentColor' />
    <circle cx='12' cy='12' r='1.8' fill='currentColor' />
    <circle cx='19' cy='12' r='1.8' fill='currentColor' />
  </svg>
);

const PICKED_ONCE_KEY = 'morningList.pickedOnce';

/** Same breakpoint as the stylesheet's desktop layout. */
const DESKTOP_QUERY = '(min-width: 960px)';

const MorningList: React.FC<Props> = ({
  data,
  update,
  post,
  initialListId,
  phone = false,
  embedded,
  onExpired,
}) => {
  const { date, language, todos, lists } = data;
  const assistant = data.assistantName
    ? firstName(data.assistantName)
    : 'your secretary';
  const suggestions = data.suggestions ?? [];

  const [listId, setListId] = useState<string>(
    () => lists.find((list) => list.id === initialListId)?.id ?? TODAY,
  );
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [knowsPicking, setKnowsPicking] = useState(() => {
    try {
      return localStorage.getItem(PICKED_ONCE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [othersOpen, setOthersOpen] = useState(false);
  const [spottedOpen, setSpottedOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [naming, setNaming] = useState(false);
  const [listName, setListName] = useState('');
  const [people, setPeople] = useState<WaBriefingAssignable[] | null>(null);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [offer, setOffer] = useState<WaBriefingFindTimeData | null>(null);
  const [finding, setFinding] = useState<'parts' | 'slot' | null>(null);
  const [choosingTime, setChoosingTime] = useState(false);
  const [findError, setFindError] = useState<string | null>(null);
  const findSeq = useRef(0);
  const [part, setPart] = useState<WaBriefingDayPart | null>(null);
  const [whenDays, setWhenDays] = useState(false);
  const [desktopViewport, setDesktop] = useState(
    () => !phone && (window.matchMedia?.(DESKTOP_QUERY).matches ?? false),
  );
  const desktop = !phone && desktopViewport;
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [subOpen, setSubOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameTo, setRenameTo] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (phone) return;
    const mq = window.matchMedia?.(DESKTOP_QUERY);
    if (!mq) return;
    const onChange = () => setDesktop(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [phone]);

  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : true) {
        const target = e.target as Element | null;
        if (e instanceof MouseEvent && target?.closest('.ml__menu-wrap')) {
          return;
        }
        setMenuFor(null);
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [menuFor]);

  useEffect(() => setSubOpen(false), [menuFor]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (mode !== 'ask') return;
    document
      .querySelector('.ml__ask')
      ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }, [mode]);

  const own = (t: WaBriefingTodo) => !t.assignee;
  const inToday = (t: WaBriefingTodo) => !!t.dueDate && t.dueDate <= date;
  const inLater = (t: WaBriefingTodo) => !t.listId && !inToday(t);
  const onView = (t: WaBriefingTodo, view: string) =>
    view === TODAY
      ? inToday(t)
      : view === LATER
        ? inLater(t)
        : t.listId === view;

  const rows = useMemo(
    () =>
      todos
        .filter((t) => own(t) && !t.done && onView(t, listId))
        .sort((a, b) => (a.dueDate ?? '9').localeCompare(b.dueDate ?? '9')),
    [todos, listId, date],
  );
  const waiting = useMemo(
    () => todos.filter((t) => !!t.assignee && !t.done && onView(t, listId)),
    [todos, listId, date],
  );
  const fromLater =
    listId === TODAY && rows.length === 0
      ? todos.filter((t) => own(t) && !t.done && inLater(t)).slice(0, 2)
      : [];

  const openCount = (view: string) =>
    todos.filter((t) => own(t) && !t.done && onView(t, view)).length;

  const allLists = [
    { id: TODAY, name: 'Today' },
    ...lists,
    { id: LATER, name: 'Later' },
  ];
  const currentName = allLists.find((l) => l.id === listId)?.name ?? 'Today';
  const listNameOf = (id: string | null) =>
    lists.find((l) => l.id === id)?.name ?? null;

  const pickedTodo = todos.find((t) => t.id === picked && !t.done) ?? null;
  const pickedIsTheirs = !!pickedTodo?.assignee;
  const pickable = (t: WaBriefingTodo) => !t.done;

  const stopFinding = () => {
    findSeq.current += 1;
    setFinding(null);
    setFindError(null);
  };

  const clearPick = () => {
    setPicked(null);
    setMode(null);
    setChosen(null);
    setQuery('');
    setNote('');
    setOffer(null);
    setPart(null);
    stopFinding();
    setWhenDays(false);
  };

  const selectList = (id: string) => {
    setListId(id);
    setSwitcherOpen(false);
    setAdding(false);
    clearPick();
  };

  const pick = (todo: WaBriefingTodo) => {
    if (picked === todo.id) return clearPick();
    clearPick();
    setPicked(todo.id);
    if (!knowsPicking) {
      setKnowsPicking(true);
      try {
        localStorage.setItem(PICKED_ONCE_KEY, '1');
      } catch {
        // Private mode: the hint just shows again next time.
      }
    }
  };

  /** Runs a call; false (with the error shown) when it did not go through. */
  const call = async <T,>(
    path: string,
    body: Record<string, unknown>,
  ): Promise<(T & { error?: string }) | null> => {
    setBusy(true);
    try {
      const { ok, status, payload } = await post<T>(path, body);
      if (status === 410 && onExpired) {
        onExpired();
        return null;
      }
      if (!ok || !payload) {
        setToast(payload?.error ?? 'That didn’t work. Try again.');
        return null;
      }
      return payload;
    } catch {
      setToast('That didn’t work. Try again.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const todoCall = async (path: string, body: Record<string, unknown>) => {
    const payload = await call<WaBriefingTodoResponse>(path, body);
    if (!payload?.data) return false;
    update({ todos: payload.data.todos });
    return true;
  };

  const toggleDone = (todo: WaBriefingTodo) =>
    void todoCall('todo', { todoId: todo.id, done: !todo.done });

  const setDue = async (todo: WaBriefingTodo, dueDate: string | null) => {
    const ok = await todoCall('todo', { todoId: todo.id, dueDate });
    if (ok) clearPick();
  };

  const moveTo = async (todo: WaBriefingTodo, to: string | null) => {
    const ok = await todoCall('todo', { todoId: todo.id, listId: to });
    if (ok) clearPick();
  };

  const addTodo = async () => {
    const title = newTitle.trim();
    if (!title || busy) return;
    const onList = listId !== TODAY && listId !== LATER ? listId : null;
    const ok = await todoCall('add', {
      title,
      listId: onList,
      dueDate: listId === TODAY ? date : null,
    });
    if (ok) setNewTitle('');
  };

  const createList = async () => {
    const name = listName.trim();
    if (!name || busy) return;
    const payload = await call<WaBriefingListResponse>('list', { name });
    if (!payload?.data) return;
    update({ lists: payload.data.lists });
    setListName('');
    setNaming(false);
    selectList(payload.data.listId);
    setAdding(true);
  };

  // Desktop: a sheet closing ends the action; there is no picked row to keep.
  useEffect(() => {
    if (desktop && !mode && picked) setPicked(null);
  }, [desktop, mode, picked]);

  const fromMenu = (todo: WaBriefingTodo, next: Mode) => {
    clearPick();
    setMenuFor(null);
    setPicked(todo.id);
    setMode(next);
    if (next === 'who' && people === null) {
      void call<WaBriefingPeopleResponse>('people', {}).then((payload) =>
        setPeople(payload?.data?.people ?? []),
      );
    }
    if (next === 'when' && inToday(todo)) void findTime(todo, null);
  };

  const canReword = (t: WaBriefingTodo) => !t.from && !t.done;

  const startEdit = (todo: WaBriefingTodo) => {
    if (!canReword(todo)) return;
    setMenuFor(null);
    setEditing(todo.id);
    setEditTitle(todo.title);
  };

  const saveTitle = async (todo: WaBriefingTodo) => {
    const title = editTitle.trim();
    setEditing(null);
    if (!title || title === todo.title) return;
    await todoCall('todo', { todoId: todo.id, title });
  };

  const isOwnList = listId !== TODAY && listId !== LATER;

  const renameList = async () => {
    const name = renameTo.trim();
    setRenaming(false);
    if (!name || name === currentName) return;
    const payload = await call<{ data?: { lists: WaBriefingList[] } }>(
      'list/rename',
      { listId, name },
    );
    if (payload?.data) update({ lists: payload.data.lists });
  };

  const deleteList = async () => {
    const payload = await call<{
      data?: { lists: WaBriefingList[]; todos: WaBriefingTodo[] };
    }>('list/delete', { listId });
    setDeleting(false);
    if (!payload?.data) return;
    update({ lists: payload.data.lists, todos: payload.data.todos });
    selectList(TODAY);
  };

  const startMode = (next: Mode) => {
    if (mode === next) {
      setMode(null);
      return;
    }
    setMode(next);
    setChosen(null);
    setQuery('');
    setNote('');
    setOffer(null);
    setPart(null);
    stopFinding();
    setWhenDays(false);
    if (next === 'who' && people === null) {
      void call<WaBriefingPeopleResponse>('people', {}).then((payload) =>
        setPeople(payload?.data?.people ?? []),
      );
    }
    if (next === 'when' && pickedTodo && inToday(pickedTodo)) {
      void findTime(pickedTodo, null);
    }
  };

  const findTime = async (
    todo: WaBriefingTodo,
    within: WaBriefingDayPart | null,
    after?: string,
  ) => {
    const ask = ++findSeq.current;
    setChoosingTime(false);
    setFinding(within ? 'slot' : 'parts');
    const { ok, payload } = await post<{ data?: WaBriefingFindTimeData }>(
      'find-time',
      within ? { todoId: todo.id, part: within, after } : { todoId: todo.id },
    ).catch(() => ({ ok: false, payload: null }));
    if (ask !== findSeq.current) return;
    setFinding(null);
    if (!ok || !payload?.data) {
      setFindError('Couldn’t check your calendar. Try again.');
      return;
    }
    setFindError(null);
    // Without a part only the parts with room matter: no time is offered yet.
    setOffer(
      within
        ? payload.data
        : { ...payload.data, slot: null, parts: payload.data.parts ?? [] },
    );
  };

  const choosePart = (todo: WaBriefingTodo, next: WaBriefingDayPart) => {
    setPart(next);
    void findTime(todo, next);
  };

  const book = async (todo: WaBriefingTodo) => {
    if (!offer?.slot || busy) return;
    setBusy(true);
    setFindError(null);
    const { ok, status, payload } = await post<WaBriefingTodoResponse>('book', {
      todoId: todo.id,
      start: offer.slot.start,
    })
      .catch(() => ({ ok: false, status: 0, payload: null }))
      .finally(() => setBusy(false));
    if (status === 410 && onExpired) return onExpired();
    if (!ok || !payload?.data) {
      setFindError(payload?.error ?? 'Couldn’t book that. Try again.');
      return;
    }
    update({ todos: payload.data.todos });
    clearPick();
  };

  const handToAssistant = async (todo: WaBriefingTodo) => {
    const ok = await todoCall('delegate', {
      todoId: todo.id,
      note: note.trim() || null,
    });
    if (ok) clearPick();
  };

  const giveTo = async (todo: WaBriefingTodo, person: WaBriefingAssignable) => {
    const ok = await todoCall('assign', {
      todoId: todo.id,
      assigneeUserId: person.userId,
    });
    if (ok) clearPick();
  };

  const takeBack = async (todo: WaBriefingTodo) => {
    const ok = await todoCall('assign', {
      todoId: todo.id,
      assigneeUserId: null,
    });
    if (ok) clearPick();
  };

  const ask = async (todo: WaBriefingTodo) => {
    const ok = await todoCall('ask', {
      todoId: todo.id,
      note: note.trim() || null,
    });
    if (ok) clearPick();
  };

  const decideSuggestion = async (
    suggestion: WaBriefingSuggestion,
    action: 'add' | 'not_now',
  ) => {
    const payload = await call<WaBriefingSuggestionResponse>('suggestion', {
      suggestionId: suggestion.id,
      action,
    });
    if (!payload?.data) return;
    update({
      todos: payload.data.todos,
      suggestions: suggestions.filter(
        (s) =>
          s.id !== suggestion.id &&
          payload.data!.suggestions.some((n) => n.id === s.id),
      ),
    });
  };

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const found = (people ?? []).filter(
      (p) =>
        !q ||
        p.name
          .toLowerCase()
          .split(/\s+/)
          .some((w) => w.startsWith(q)) ||
        (p.detail ?? '').toLowerCase().includes(q),
    );
    return {
      circle: found.filter((p) => p.group === 'circle'),
      org: found.filter((p) => p.group === 'organisation'),
    };
  }, [people, query]);
  const assistantMatches =
    !query.trim() ||
    assistant.toLowerCase().startsWith(query.trim().toLowerCase());
  const chosenPerson =
    chosen && chosen !== 'assistant'
      ? ((people ?? []).find((p) => p.userId === chosen) ?? null)
      : null;

  const recentlyAsked = (t: WaBriefingTodo) =>
    !!t.askedAt && Date.now() - Date.parse(t.askedAt) < ASK_COOLDOWN_MS;

  const renderRow = (todo: WaBriefingTodo) => {
    const isPicked = picked === todo.id;
    const theirs = !!todo.assignee;
    const late = !!todo.dueDate && todo.dueDate < date;
    const inlineWho = desktop && theirs;
    const meta: string[] = [];
    if (listId === TODAY && todo.listId) {
      const name = listNameOf(todo.listId);
      if (name) meta.push(name);
    }
    if (listId === TODAY && theirs && inLater(todo)) meta.push('Later');
    if (listId !== TODAY && todo.dueDate) {
      meta.push(dueLabel(todo.dueDate, date, language));
    }
    if (todo.from) meta.push(`From ${todo.from.name}`);
    if (theirs && recentlyAsked(todo)) meta.push('Asked their secretary');
    return (
      <div
        key={todo.id}
        className={[
          'wa-todos__row',
          todo.done ? 'wa-todos__row--done' : '',
          isPicked ? 'wa-todos__row--picked' : '',
          theirs ? 'ml__row--theirs' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid='morning-list-row'
      >
        <button
          type='button'
          className='wa-todos__check'
          role='checkbox'
          aria-checked={todo.done}
          aria-label={`Done: ${todo.title}`}
          disabled={busy}
          onClick={() => toggleDone(todo)}
        >
          <Tick />
        </button>
        <div className='wa-todos__body'>
          {editing === todo.id ? (
            <form
              className='ml__edit'
              onSubmit={(e) => {
                e.preventDefault();
                void saveTitle(todo);
              }}
            >
              <input
                type='text'
                autoFocus
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={() => void saveTitle(todo)}
                onKeyDown={(e) => e.key === 'Escape' && setEditing(null)}
                aria-label='To-do wording'
                autoComplete='off'
                maxLength={500}
              />
            </form>
          ) : desktop ? (
            <span
              className='wa-todos__title ml__title-text'
              onDoubleClick={() => startEdit(todo)}
              title={canReword(todo) ? 'Double-click to edit' : undefined}
            >
              {todo.title}
            </span>
          ) : (
            <button
              type='button'
              className='wa-todos__title'
              aria-pressed={isPicked}
              disabled={!pickable(todo)}
              onClick={() => pick(todo)}
            >
              {todo.title}
            </button>
          )}
          {todo.done && (todo.delegation || todo.inHand || todo.work) && (
            <span className='wa-todos__status'>
              Done · {assistant} was on it
            </span>
          )}
          {!todo.done && todo.work && (
            <>
              <span
                className={`wa-todos__status${todo.work.state === 'working' ? ' wa-todos__status--live' : ''}`}
              >
                {workLine(todo.work.state, assistant)}
              </span>
              {todo.work.detail && (
                <span className='wa-todos__meta'>{todo.work.detail}</span>
              )}
            </>
          )}
          {!todo.done && !todo.work && todo.delegation && (
            <>
              <span
                className={`wa-todos__status${todo.delegation.started ? ' wa-todos__status--live' : ''}`}
              >
                {todo.delegation.started
                  ? `${assistant} is working on it`
                  : `Queued for ${assistant}`}
              </span>
              {todo.inHand?.what && (
                <span className='wa-todos__meta'>{todo.inHand.what}</span>
              )}
            </>
          )}
          {!todo.done && !todo.work && !todo.delegation && todo.inHand && (
            <>
              <span className='wa-todos__status wa-todos__status--live'>
                {assistant}&apos;s on it
              </span>
              {todo.inHand.what && (
                <span className='wa-todos__meta'>{todo.inHand.what}</span>
              )}
            </>
          )}
          {inlineWho && todo.assignee ? (
            <span className='wa-todos__meta ml__meta-who'>
              <Avatar
                userId={todo.assignee.userId}
                name={todo.assignee.name}
                small
              />
              {[firstName(todo.assignee.name), ...meta].join(' · ')}
            </span>
          ) : (
            meta.length > 0 && (
              <span className='wa-todos__meta'>{meta.join(' · ')}</span>
            )
          )}
          {isPicked && mode === 'ask' && todo.assignee && (
            <form
              className='wa-todos__say ml__ask'
              onSubmit={(e) => {
                e.preventDefault();
                void ask(todo);
              }}
            >
              <p className='ml__ask-note'>
                {assistant} asks {firstName(todo.assignee.name)}&apos;s
                secretary, not {firstName(todo.assignee.name)}. You hear back
                when it&apos;s done or stuck.
              </p>
              {recentlyAsked(todo) ? (
                <p className='ml__ask-note'>
                  Already asked in the last day. You&apos;ll hear back.
                </p>
              ) : (
                <div className='ml__ask-row'>
                  <input
                    type='text'
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder='Anything to add? (optional)'
                    autoComplete='off'
                    maxLength={1000}
                  />
                  <button
                    type='submit'
                    className='ml__send-text'
                    disabled={busy}
                  >
                    Ask
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
        {desktop && !todo.done && renderMenu(todo)}
        {inlineWho ? null : theirs && todo.assignee ? (
          <span className='ml__who'>
            <Avatar
              userId={todo.assignee.userId}
              name={todo.assignee.name}
              small
            />
            {firstName(todo.assignee.name)}
          </span>
        ) : todo.scheduled && inToday(todo) ? (
          <span className='wa-todos__side wa-todos__side--time'>
            {todo.scheduled.time}
          </span>
        ) : late && listId === TODAY && !todo.done ? (
          <span className='wa-todos__side wa-todos__side--late'>
            {lateLabel(todo.dueDate as string, date, language)}
          </span>
        ) : null}
      </div>
    );
  };

  const renderMenu = (todo: WaBriefingTodo) => {
    const theirs = !!todo.assignee;
    const items: Array<[string, () => void]> = theirs
      ? [
          ['Ask if it’s done', () => fromMenu(todo, 'ask')],
          [
            'Take it back',
            () => {
              setMenuFor(null);
              void takeBack(todo);
            },
          ],
        ]
      : [
          ['Assign', () => fromMenu(todo, 'who')],
          ['Schedule', () => fromMenu(todo, 'when')],
        ];
    const tail: Array<[string, () => void]> = canReword(todo)
      ? [['Edit', () => startEdit(todo)]]
      : [];
    const noList = inToday(todo) ? 'No list' : 'Later';
    const listItem = !theirs && (
      <span
        className='ml__sub-wrap'
        onMouseEnter={() => setSubOpen(true)}
        onMouseLeave={() => setSubOpen(false)}
      >
        <button
          type='button'
          role='menuitem'
          className='ml__sub-trigger'
          aria-haspopup='menu'
          aria-expanded={subOpen}
          onClick={() => setSubOpen(!subOpen)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              setSubOpen(true);
            }
          }}
        >
          <span aria-hidden='true' className='ml__chev'>
            ‹
          </span>
          List
        </button>
        {subOpen && (
          <span className='ml__menu ml__submenu' role='menu' aria-label='Lists'>
            {[...lists, { id: null, name: noList }].map((l) => (
              <button
                key={l.id ?? 'none'}
                type='button'
                role='menuitemradio'
                aria-checked={todo.listId === l.id}
                disabled={busy}
                onClick={() => {
                  setMenuFor(null);
                  void moveTo(todo, l.id);
                }}
              >
                {l.name}
                {todo.listId === l.id && <span aria-hidden='true'>✓</span>}
              </button>
            ))}
            <button
              type='button'
              role='menuitem'
              className='ml__submenu-new'
              onClick={() => {
                setMenuFor(null);
                setNaming(true);
              }}
            >
              + New list
            </button>
          </span>
        )}
      </span>
    );
    return (
      <span className='ml__menu-wrap'>
        <button
          type='button'
          className='ml__dots'
          aria-label={`Options for ${todo.title}`}
          aria-haspopup='menu'
          aria-expanded={menuFor === todo.id}
          onClick={() => setMenuFor(menuFor === todo.id ? null : todo.id)}
        >
          <Dots />
        </button>
        {menuFor === todo.id && (
          <span className='ml__menu' role='menu'>
            {items.map(([label, run]) => (
              <button key={label} type='button' role='menuitem' onClick={run}>
                {label}
              </button>
            ))}
            {listItem}
            {tail.map(([label, run]) => (
              <button key={label} type='button' role='menuitem' onClick={run}>
                {label}
              </button>
            ))}
          </span>
        )}
      </span>
    );
  };

  const renderNewList = () => (
    <div className='ml__scrim' onClick={() => setNaming(false)}>
      <form
        className='ml__sheet ml__dialog'
        role='dialog'
        aria-label='New list'
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          void createList();
        }}
      >
        <h2 className='ml__sheet-title'>New list</h2>
        <p className='ml__sheet-sub'>
          Only you see it. You can still give single to-dos on it to someone.
        </p>
        <input
          className='ml__field'
          type='text'
          autoFocus
          value={listName}
          onChange={(e) => setListName(e.target.value)}
          placeholder='e.g. Ski trip'
          aria-label='New list name'
          autoComplete='off'
          maxLength={100}
        />
        <div className='ml__dialog-actions'>
          <button
            type='button'
            className='ml__quiet'
            onClick={() => setNaming(false)}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='ml__go'
            disabled={!listName.trim() || busy}
          >
            Create list
          </button>
        </div>
      </form>
    </div>
  );

  const ownOnList = todos.filter(
    (t) => t.listId === listId && !t.done && !t.assignee && !t.from,
  ).length;
  const othersOnList = todos.filter(
    (t) => t.listId === listId && !t.done && (!!t.assignee || !!t.from),
  ).length;

  const renderDelete = () => (
    <div className='ml__scrim' onClick={() => setDeleting(false)}>
      <div
        className='ml__sheet ml__dialog'
        role='alertdialog'
        aria-label={`Delete ${currentName}?`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className='ml__sheet-title'>Delete {currentName}?</h2>
        <p className='ml__sheet-sub'>
          {ownOnList > 0
            ? `Your ${ownOnList} unfinished to-do${ownOnList === 1 ? '' : 's'} on it will be deleted too.`
            : 'There’s nothing unfinished of yours on it.'}
          {othersOnList > 0 &&
            ` The ${othersOnList} shared with someone else move${othersOnList === 1 ? 's' : ''} to Later.`}
        </p>
        <div className='ml__dialog-actions'>
          <button
            type='button'
            className='ml__quiet'
            onClick={() => setDeleting(false)}
          >
            Keep it
          </button>
          <button
            type='button'
            className='ml__go'
            disabled={busy}
            onClick={() => void deleteList()}
          >
            Delete list
          </button>
        </div>
      </div>
    </div>
  );

  const renderSwitcher = (className: string) => (
    <div className={className} role='group' aria-label='Your lists'>
      {allLists.map((l) => (
        <button
          key={l.id}
          type='button'
          className={`ml__switch${l.id === LATER ? ' ml__switch--later' : ''}`}
          aria-pressed={l.id === listId}
          onClick={() => selectList(l.id)}
        >
          <span className='ml__switch-name'>{l.name}</span>
          <span className='ml__switch-count'>{openCount(l.id)}</span>
        </button>
      ))}
      {naming && !desktop ? (
        <form
          className='ml__switch ml__switch--naming'
          onSubmit={(e) => {
            e.preventDefault();
            void createList();
          }}
        >
          <input
            type='text'
            autoFocus
            value={listName}
            onChange={(e) => setListName(e.target.value)}
            onBlur={() => !listName.trim() && setNaming(false)}
            placeholder='Name your list'
            aria-label='New list name'
            autoComplete='off'
            maxLength={120}
          />
          <button type='submit' disabled={!listName.trim() || busy}>
            Create
          </button>
        </form>
      ) : (
        <button
          type='button'
          className='ml__switch ml__switch--new'
          onClick={() => setNaming(true)}
        >
          + New list
        </button>
      )}
    </div>
  );

  const renderPerson = (p: WaBriefingAssignable) => (
    <button
      key={p.userId}
      type='button'
      className='ml__person'
      aria-pressed={chosen === p.userId}
      onClick={() => setChosen(p.userId)}
    >
      <Avatar userId={p.userId} name={p.name} />
      <span className='ml__person-text'>
        <span className='ml__person-name'>{p.name}</span>
        {p.detail && <span className='ml__person-sub'>{p.detail}</span>}
      </span>
    </button>
  );

  const renderWho = (todo: WaBriefingTodo) => (
    <div className='ml__scrim' onClick={() => setMode(null)}>
      <div
        className='ml__sheet'
        role='dialog'
        aria-label='Who’s doing it?'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='ml__grab' />
        <h2 className='ml__sheet-title'>Who&apos;s doing it?</h2>
        <p className='ml__sheet-sub'>{todo.title}</p>
        <label className='ml__search'>
          <Search />
          <input
            type='text'
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setChosen(null);
            }}
            placeholder='Search your Circle and organisation'
            autoComplete='off'
          />
        </label>
        <div className='ml__people'>
          {assistantMatches && (
            <button
              type='button'
              className='ml__person'
              aria-pressed={chosen === 'assistant'}
              onClick={() => setChosen('assistant')}
            >
              <DreamMarque className='ml__avatar-marque' aria-hidden='true' />
              <span className='ml__person-text'>
                <span className='ml__person-name'>{assistant}</span>
                <span className='ml__person-sub'>
                  Your secretary does it for you
                </span>
              </span>
            </button>
          )}
          {candidates.circle.length > 0 && (
            <>
              <h3 className='ml__group'>Your Circle</h3>
              {candidates.circle.map(renderPerson)}
            </>
          )}
          {candidates.org.length > 0 && (
            <>
              <h3 className='ml__group'>Your organisation</h3>
              {candidates.org.map(renderPerson)}
            </>
          )}
          {people === null && <p className='ml__empty'>Loading…</p>}
          {people !== null &&
            !candidates.circle.length &&
            !candidates.org.length &&
            !assistantMatches && (
              <p className='ml__empty'>
                No one called &ldquo;{query}&rdquo; in your Circle or
                organisation.
              </p>
            )}
        </div>
        <div className='ml__sheet-foot'>
          {chosen === 'assistant' ? (
            <input
              className='ml__note'
              type='text'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={`Anything ${assistant} should know?`}
              autoComplete='off'
              maxLength={1000}
            />
          ) : (
            chosenPerson && (
              <p className='ml__consequence'>
                {firstName(chosenPerson.name)} gets it in their morning list,
                marked as from you. You see it under Assigned to others, and it
                ticks off when they&apos;re done.
              </p>
            )
          )}
          <button
            type='button'
            className='ml__primary'
            disabled={!chosen || busy}
            onClick={() => {
              if (chosen === 'assistant') void handToAssistant(todo);
              else if (chosenPerson) void giveTo(todo, chosenPerson);
            }}
          >
            {chosen === 'assistant'
              ? busy
                ? 'Handing over…'
                : `Hand to ${assistant}`
              : chosenPerson
                ? busy
                  ? 'Giving…'
                  : `Give to ${firstName(chosenPerson.name)}`
                : 'Choose someone'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDays = (todo: WaBriefingTodo) => {
    const choices: { name: string; due: string | null }[] = [
      { name: 'Today', due: date },
      { name: 'Tomorrow', due: addDays(date, 1) },
      { name: 'This weekend', due: thisWeekend(date) },
      { name: 'Next week', due: nextWeek(date) },
      { name: 'No date', due: null },
    ].filter((c) => !(inToday(todo) && c.due === date));
    return (
      <>
        {choices.map((c) => (
          <button
            key={c.name}
            type='button'
            className='ml__setting'
            aria-pressed={todo.dueDate === c.due}
            disabled={busy}
            onClick={() => void setDue(todo, c.due)}
          >
            {c.name}
            {todo.dueDate === c.due && <span aria-hidden='true'>✓</span>}
          </button>
        ))}
        <label className='ml__setting ml__date-pick'>
          Pick a date
          <input
            type='date'
            min={date}
            value={todo.dueDate ?? ''}
            onChange={(e) =>
              e.target.value && void setDue(todo, e.target.value)
            }
          />
        </label>
      </>
    );
  };

  const renderFindTime = (todo: WaBriefingTodo) => (
    <>
      <div
        className='wa-todos__parts ml__parts'
        role='group'
        aria-label='Find time today'
        data-loading={finding === 'parts' || undefined}
      >
        {DAY_PARTS.filter((p) => !offer || offer.parts.includes(p.id)).map(
          (p) => (
            <button
              key={p.id}
              type='button'
              className='wa-todos__part'
              aria-pressed={part === p.id}
              aria-busy={finding === 'parts' || undefined}
              disabled={!!offer && busy}
              onClick={() => offer && part !== p.id && choosePart(todo, p.id)}
            >
              {p.name}
            </button>
          ),
        )}
      </div>
      {offer && offer.parts.length === 0 && offer.reason === 'no_free_time' && (
        <p className='ml__empty'>No free time left today.</p>
      )}
      {finding === 'parts' && (
        <p className='ml__finding' role='status'>
          Checking your calendar…
        </p>
      )}
      {offer &&
        !offer.slot &&
        offer.reason &&
        offer.reason !== 'no_free_time' && (
          <p className='ml__empty'>
            {offer.reason === 'no_calendar'
              ? 'Connect a calendar to find time for it.'
              : 'Your calendar isn’t answering right now.'}
          </p>
        )}
      {part && (finding === 'slot' || offer) && (
        <div className='wa-todos__offer ml__offer'>
          {finding === 'slot' ? (
            <p className='ml__finding' role='status'>
              Finding a time…
            </p>
          ) : offer?.slot ? (
            <>
              <p>
                How about{' '}
                <button
                  type='button'
                  className='ml__word'
                  aria-label='Choose another time'
                  aria-expanded={choosingTime}
                  disabled={busy || (offer.options ?? []).length < 2}
                  onClick={() => setChoosingTime((v) => !v)}
                >
                  {offer.slot.time}
                </button>
                {offer.context ? `, ${offer.context}` : ''}?
              </p>
              {choosingTime && desktop && renderTimes(todo, 'drop')}
              <div className='wa-todos__offer-actions'>
                <button
                  type='button'
                  className='wa-todos__book'
                  disabled={busy}
                  aria-busy={busy || undefined}
                  onClick={() => void book(todo)}
                >
                  {busy ? 'Booking…' : 'Book it'}
                </button>
              </div>
            </>
          ) : (
            <p>No free time left then.</p>
          )}
        </div>
      )}
      {(findError || toast) && (
        <p className='ml__empty ml__hint--error' role='alert'>
          {findError ?? toast}
        </p>
      )}
      <button
        type='button'
        className='ml__setting ml__another'
        onClick={() => setWhenDays(true)}
      >
        Another day
        <span aria-hidden='true'>›</span>
      </button>
    </>
  );

  const renderTimes = (todo: WaBriefingTodo, variant: 'drop' | 'sheet') => (
    <ul
      className={`ml__times ml__times--${variant}`}
      aria-label='Other free times'
    >
      {(offer?.options ?? []).map((o) => {
        const current = o.slot.start === offer?.slot?.start;
        return (
          <li key={o.slot.start}>
            <button
              type='button'
              className='ml__time-option'
              aria-pressed={current}
              onClick={() => {
                if (!offer) return;
                setOffer({
                  ...offer,
                  slot: o.slot,
                  context: o.context,
                });
                setChoosingTime(false);
              }}
            >
              <span className='ml__time-option-time'>{o.slot.time}</span>
              {o.context && (
                <span className='ml__time-option-context'>{o.context}</span>
              )}
              {current && <Tick />}
            </button>
          </li>
        );
      })}
    </ul>
  );

  const renderWhen = (todo: WaBriefingTodo) => {
    const other = todo.from ?? todo.assignee;
    const days = whenDays || !inToday(todo);
    const pickingTime = choosingTime && !desktop && !days;
    return (
      <div className='ml__scrim' onClick={() => setMode(null)}>
        <div
          className='ml__sheet'
          role='dialog'
          aria-label='When?'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='ml__grab' />
          <h2 className='ml__sheet-title'>
            {(whenDays || pickingTime) && (
              <button
                type='button'
                className='ml__back'
                aria-label='Back'
                onClick={() =>
                  pickingTime ? setChoosingTime(false) : setWhenDays(false)
                }
              >
                ‹
              </button>
            )}
            {pickingTime ? 'Pick a time' : whenDays ? 'Another day' : 'When?'}
          </h2>
          <p className='ml__sheet-sub'>
            {todo.title}
            {days &&
              other &&
              ` · ${firstName(other.name)} sees the new date too`}
          </p>
          <div className='ml__people'>
            {days
              ? renderDays(todo)
              : pickingTime
                ? renderTimes(todo, 'sheet')
                : renderFindTime(todo)}
          </div>
        </div>
      </div>
    );
  };

  const renderMove = (todo: WaBriefingTodo) => {
    const noList = inToday(todo) ? 'No list, just Today' : 'Later';
    return (
      <div className='ml__scrim' onClick={() => setMode(null)}>
        <div
          className='ml__sheet'
          role='dialog'
          aria-label='Which list?'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='ml__grab' />
          <h2 className='ml__sheet-title'>Which list?</h2>
          <p className='ml__sheet-sub'>
            {todo.title}
            {todo.from &&
              ` · only moves it for you, not for ${firstName(todo.from.name)}`}
          </p>
          <div className='ml__people'>
            {[...lists, { id: null, name: noList }].map((l) => (
              <button
                key={l.id ?? 'none'}
                type='button'
                className='ml__setting'
                aria-pressed={todo.listId === l.id}
                disabled={busy}
                onClick={() => void moveTo(todo, l.id)}
              >
                {l.name}
                {todo.listId === l.id && <span aria-hidden='true'>✓</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`wa-briefing wa-briefing--todos ml${phone ? ' wa-briefing--phone' : ''}${embedded ? ' ml--embedded' : ''}`}
      data-testid='morning-list'
    >
      <main className='wa-briefing__app'>
        <nav className='ml__side' aria-label='Lists'>
          {renderSwitcher('ml__side-lists')}
        </nav>
        <section
          className={`wa-todos${pickedTodo ? ' wa-todos--picking' : ''}`}
        >
          {!embedded && (
            <div className='wa-todos__top ml__top'>
              <DreamMarque className='wa-todos__marque' aria-hidden='true' />
              <a
                className='ml__settings'
                aria-label='Settings'
                href='/settings'
              >
                <Gear />
              </a>
            </div>
          )}

          {renaming ? (
            <form
              className='ml__rename'
              onSubmit={(e) => {
                e.preventDefault();
                void renameList();
              }}
            >
              <input
                type='text'
                autoFocus
                value={renameTo}
                onChange={(e) => setRenameTo(e.target.value)}
                onBlur={() => void renameList()}
                onKeyDown={(e) => e.key === 'Escape' && setRenaming(false)}
                aria-label='List name'
                autoComplete='off'
                maxLength={100}
              />
            </form>
          ) : (
            <div className='ml__heading'>
              <h1
                className='wa-todos__title-day ml__title'
                data-testid='morning-list-title'
              >
                <button
                  type='button'
                  className='wa-todos__day-button'
                  aria-expanded={switcherOpen}
                  onClick={() => setSwitcherOpen((o) => !o)}
                >
                  {currentName}
                  <Caret />
                </button>
              </h1>
              {desktop && isOwnList && (
                <span className='ml__list-actions'>
                  <button
                    type='button'
                    onClick={() => {
                      setRenameTo(currentName);
                      setRenaming(true);
                    }}
                  >
                    Rename
                  </button>
                  <button type='button' onClick={() => setDeleting(true)}>
                    Delete
                  </button>
                </span>
              )}
            </div>
          )}

          {switcherOpen && renderSwitcher('ml__switcher')}

          <div className='wa-todos__list' key={listId}>
            {rows.map(renderRow)}
            {adding ? (
              <form
                className='ml__add ml__add--open'
                onSubmit={(e) => {
                  e.preventDefault();
                  void addTodo();
                }}
              >
                <span className='ml__add-plus' aria-hidden='true'>
                  +
                </span>
                <input
                  type='text'
                  autoFocus
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onBlur={() => !newTitle.trim() && setAdding(false)}
                  placeholder={
                    listId === TODAY
                      ? 'Add a to-do for today'
                      : listId === LATER
                        ? 'Add something for later'
                        : `Add to ${currentName}`
                  }
                  aria-label='New to-do'
                  autoComplete='off'
                  maxLength={500}
                />
              </form>
            ) : (
              <button
                type='button'
                className='ml__add'
                onClick={() => setAdding(true)}
              >
                <span className='ml__add-plus' aria-hidden='true'>
                  +
                </span>
                Add a to-do
              </button>
            )}

            {fromLater.length > 0 && (
              <section className='wa-todos__ahead'>
                <h2 className='wa-todos__ahead-head'>
                  All done. One from Later?
                </h2>
                {fromLater.map(renderRow)}
              </section>
            )}

            {listId === TODAY && suggestions.length > 0 && (
              <section
                className={`wa-todos__spotted${spottedOpen ? ' wa-todos__spotted--open' : ''}`}
              >
                <h2 className='wa-todos__spotted-head'>
                  <button
                    type='button'
                    aria-expanded={spottedOpen}
                    onClick={() => setSpottedOpen((o) => !o)}
                  >
                    <DreamMarque
                      variant='optical-sm'
                      className='wa-todos__spotted-mark'
                      aria-hidden='true'
                    />
                    <span className='wa-todos__spotted-label'>
                      Spotted in your email
                      <span className='wa-todos__spotted-sub'>
                        {suggestions.length}
                      </span>
                    </span>
                    <Caret />
                  </button>
                </h2>
                {spottedOpen && (
                  <div className='wa-todos__spotted-body'>
                    {suggestions.map((s) => (
                      <div
                        key={s.id}
                        className='wa-todos__row wa-todos__row--suggestion'
                      >
                        <span className='wa-todos__bullet' aria-hidden='true' />
                        <div className='wa-todos__body'>
                          <span className='wa-todos__title'>{s.title}</span>
                          <span className='wa-todos__meta'>{s.detail}</span>
                          <button
                            type='button'
                            className='wa-todos__not-now'
                            disabled={busy}
                            onClick={() => void decideSuggestion(s, 'not_now')}
                          >
                            Not now
                          </button>
                        </div>
                        <button
                          type='button'
                          className='wa-todos__add'
                          disabled={busy}
                          onClick={() => void decideSuggestion(s, 'add')}
                        >
                          {s.target === 'calendar'
                            ? 'Add to calendar'
                            : s.doOn && s.doOn > date
                              ? `Add for ${formatDay(s.doOn, language, { weekday: 'short', day: 'numeric', month: 'short' })}`
                              : 'Add to today'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {waiting.length > 0 && (
              <section className='wa-todos__ahead ml__others'>
                <button
                  type='button'
                  className='ml__others-head'
                  aria-expanded={othersOpen}
                  onClick={() => {
                    if (othersOpen && pickedIsTheirs) clearPick();
                    setOthersOpen(!othersOpen);
                  }}
                >
                  <span>Assigned to others</span>
                  <span className='ml__others-count'>
                    {waiting.length}
                    <Caret />
                  </span>
                </button>
                {othersOpen && waiting.map(renderRow)}
              </section>
            )}
          </div>

          <footer className='wa-todos__foot'>
            {(toast || (!desktop && (pickedTodo || !knowsPicking))) && (
              <p
                className={`wa-todos__hint${toast ? ' ml__hint--error' : ''}`}
                role={toast ? 'alert' : undefined}
              >
                {toast ??
                  (pickedTodo ? pickedTodo.title : 'Tap a to-do for options')}
              </p>
            )}
            {desktop ? null : pickedIsTheirs && pickedTodo ? (
              <div
                className='wa-todos__bar ml__bar ml__bar--two'
                role='toolbar'
              >
                <button
                  type='button'
                  aria-pressed={mode === 'ask'}
                  onClick={() => startMode('ask')}
                >
                  Ask if it&apos;s done
                </button>
                <button
                  type='button'
                  disabled={busy}
                  onClick={() => void takeBack(pickedTodo)}
                >
                  Take it back
                </button>
              </div>
            ) : (
              <div className='wa-todos__bar ml__bar' role='toolbar'>
                <button
                  type='button'
                  aria-pressed={mode === 'who'}
                  disabled={!pickedTodo}
                  onClick={() => startMode('who')}
                >
                  Who
                </button>
                <button
                  type='button'
                  aria-pressed={mode === 'when'}
                  disabled={!pickedTodo}
                  onClick={() => startMode('when')}
                >
                  When
                </button>
                <button
                  type='button'
                  aria-pressed={mode === 'list'}
                  disabled={!pickedTodo}
                  onClick={() => startMode('list')}
                >
                  List
                </button>
              </div>
            )}
          </footer>
        </section>
      </main>

      {pickedTodo && !pickedIsTheirs && mode === 'who' && renderWho(pickedTodo)}
      {pickedTodo &&
        !pickedIsTheirs &&
        mode === 'when' &&
        renderWhen(pickedTodo)}
      {pickedTodo &&
        !pickedIsTheirs &&
        mode === 'list' &&
        renderMove(pickedTodo)}
      {naming && desktop && renderNewList()}
      {deleting && renderDelete()}
    </div>
  );
};

export default MorningList;
